// Planeten als Pixelkugeln — Licht, Schatten, Oberfläche, Atmosphäre.
//
// Kein Bild-Asset und kein 3D-Werkzeug: die Kugel wird **einmal** je Planet in
// ein Pixelfeld gerechnet und danach als Textur gezeichnet (FG-10, FG-40). Der
// Eindruck von Tiefe kommt aus der Rechnung — Lambert-Licht von links oben,
// eine Oberfläche, die auf Länge und Breite der Kugel abgebildet ist, ein
// Saum aus Atmosphäre und eine gerasterte Schattengrenze statt eines weichen
// Verlaufs. Das hält den Pixel-Look-Vertrag: die Kugel ist so pixelig wie das
// Schiff, und sie wird nur **ganzzahlig** skaliert (`PLANET_TEXEL`).
//
// Die Rechnung ist rein und ohne Phaser — prüfbar in Node, wie die Bauteile der
// Basis. Die Szene legt das Ergebnis nur noch in eine Textur.

import { clamp } from '../core/math';
import { rngFrom } from '../core/rng';
import type { Planet } from '../data/types';

/** Welteinheiten je Texel. Ganzzahlig, damit jedes Texel gleich groß ist. */
export const PLANET_TEXEL = 4;

/** Der Saum der Atmosphäre, als Anteil des Radius. */
const RIM = 0.14;

/** Die Lichtrichtung — von links oben, leicht zum Betrachter. */
const LICHT = (() => {
  const v = [-0.56, -0.62, 0.55];
  const l = Math.hypot(v[0], v[1], v[2]);
  return [v[0] / l, v[1] / l, v[2] / l] as const;
})();

/** 4×4-Bayer-Matrix für die gerasterte Schattengrenze. */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map((v) => (v + 0.5) / 16);

/** Wie viele Helligkeitsstufen das Licht kennt — Pixelgrafik hat keine stufenlosen Verläufe. */
const STUFEN = 7;

export const planetTextureKey = (id: string): string => `planet:${id}`;

/** Der Radius der Kugel in Texeln — der Planet ist `PLANET_TEXEL` Welteinheiten je Texel. */
export const planetTexelRadius = (planet: Planet): number => Math.round(planet.radius / PLANET_TEXEL);

/** Kantenlänge der Textur samt Atmosphäresaum. */
export const planetTextureSize = (planet: Planet): number => {
  const r = planetTexelRadius(planet);
  return 2 * (r + Math.ceil(r * RIM));
};

const hex = (text: string): [number, number, number] => {
  const n = parseInt(text.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

const mix = (a: readonly number[], b: readonly number[], t: number): [number, number, number] =>
  [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

/**
 * Wertrauschen auf einem Gitter, das in der Länge umläuft.
 *
 * Ein vorab gezogenes Gitter statt einer Streufunktion je Abfrage: bei einer
 * Viertelmillion Texeln je Planet ist das der Unterschied zwischen einem Start
 * und einem Hänger.
 */
class Noise {
  private readonly cells: Float64Array;

  constructor(seed: number, private readonly w: number, private readonly h: number) {
    const rng = rngFrom(seed);
    this.cells = new Float64Array(w * h);
    for (let i = 0; i < this.cells.length; i++) this.cells[i] = rng();
  }

  /**
   * Beide Richtungen laufen um. Eine geklemmte Breite fiele in den feinen
   * Oktaven auf **eine** Gitterzeile zusammen — sichtbar als senkrechte
   * Streifen über die ganze Kugel. Die Naht an den Polen sieht niemand: die
   * Kugel zeigt dem Betrachter ihren Äquator.
   */
  private at(x: number, y: number): number {
    const xi = ((x % this.w) + this.w) % this.w;
    const yi = ((y % this.h) + this.h) % this.h;
    return this.cells[yi * this.w + xi];
  }

  /** Bilineare Abfrage mit weicher Kurve. */
  value(u: number, v: number): number {
    const x0 = Math.floor(u);
    const y0 = Math.floor(v);
    const fx = u - x0;
    const fy = v - y0;
    const sx = fx * fx * (3 - 2 * fx);
    const sy = fy * fy * (3 - 2 * fy);
    const a = this.at(x0, y0) + (this.at(x0 + 1, y0) - this.at(x0, y0)) * sx;
    const b = this.at(x0, y0 + 1) + (this.at(x0 + 1, y0 + 1) - this.at(x0, y0 + 1)) * sx;
    return a + (b - a) * sy;
  }

  /** Mehrere Oktaven übereinander — die Grundform und ihre Körnung. */
  fbm(u: number, v: number, oktaven: number): number {
    let summe = 0;
    let gewicht = 0;
    let amp = 1;
    let f = 1;
    for (let i = 0; i < oktaven; i++) {
      summe += this.value(u * f, v * f) * amp;
      gewicht += amp;
      amp *= 0.5;
      f *= 2;
    }
    return summe / gewicht;
  }
}

/** Was die Oberfläche an einem Punkt der Kugel ist: Farbe und Eigenleuchten. */
interface Fleck {
  rgb: [number, number, number];
  /** 0…1 — leuchtet ohne Licht weiter (Lava, Städte). */
  glut: number;
}

/**
 * Die Oberfläche je Ansicht. Ein unbekannter Name fällt auf Fels zurück —
 * still, wie ein unbekanntes Icon, und darum hält ein Gate die Tabelle gegen
 * `looks` (siehe Tests).
 */
function fleck(look: string, n: Noise, u: number, v: number, farben: Farben): Fleck {
  if (look === 'gas') {
    const band = n.fbm(u * 0.25 + n.fbm(u, v * 4, 2) * 2.2, v * 7, 3);
    return { rgb: mix(farben.dunkel, farben.hell, clamp(band * 1.6 - 0.3, 0, 1)), glut: 0 };
  }
  const h = n.fbm(u, v, 4);
  if (look === 'ice') {
    const riss = Math.abs(n.fbm(u * 2.3, v * 2.3, 3) - 0.5) < 0.035;
    const basis = mix(farben.dunkel, farben.hell, clamp(h * 1.5 - 0.15, 0, 1));
    return { rgb: riss ? mix(basis, [255, 255, 255], 0.6) : basis, glut: 0 };
  }
  if (look === 'lava') {
    const riss = Math.abs(n.fbm(u * 1.7, v * 1.7, 3) - 0.5);
    if (riss < 0.045) return { rgb: farben.glow, glut: 1 - riss / 0.045 };
    return { rgb: mix(farben.dunkel, farben.hell, clamp(h * 1.2 - 0.25, 0, 1)), glut: 0 };
  }
  if (look === 'garden') {
    const land = h > 0.5;
    const meer = mix([20, 44, 80], [36, 86, 140], clamp(h * 2, 0, 1));
    const gruen = mix(farben.dunkel, farben.hell, clamp((h - 0.5) * 3, 0, 1));
    return { rgb: land ? gruen : meer, glut: 0 };
  }
  // Fels: Grundton plus Krater — dunkle Flecken mit hellem Rand.
  const krater = n.fbm(u * 3.1 + 17, v * 3.1, 2);
  const basis = mix(farben.dunkel, farben.hell, clamp(h * 1.4 - 0.2, 0, 1));
  if (krater > 0.7) return { rgb: mix(basis, farben.dunkel, 0.6), glut: 0 };
  if (krater > 0.66) return { rgb: mix(basis, [255, 240, 220], 0.25), glut: 0 };
  return { rgb: basis, glut: 0 };
}

interface Farben {
  hell: [number, number, number];
  dunkel: [number, number, number];
  glow: [number, number, number];
}

/**
 * Die Kugel eines Planeten als RGBA-Pixel — `size × size`, zeilenweise.
 *
 * Deterministisch aus Tabellenzeile und Position: derselbe Planet sieht in
 * jedem Browser gleich aus, wie ein Brocken nach jedem Neuladen.
 */
export function planetPixels(planet: Planet, index: number): { size: number; data: Uint8ClampedArray } {
  const r = planetTexelRadius(planet);
  const size = planetTextureSize(planet);
  const mitte = size / 2;
  const data = new Uint8ClampedArray(size * size * 4);
  const farben: Farben = { hell: hex(planet.surface), dunkel: hex(planet.dark), glow: hex(planet.glow) };
  const n = new Noise(0x9a1e + index * 7919, 48, 24);
  const saum = r * (1 + RIM);

  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const dx = (x + 0.5 - mitte) / r;
      const dy = (y + 0.5 - mitte) / r;
      const d2 = dx * dx + dy * dy;
      const i = (y * size + x) * 4;
      if (d2 > 1) {
        // Die Atmosphäre: ein Saum, der nach außen ausläuft — auf der Lichtseite heller.
        const d = Math.sqrt(d2) * r;
        if (d > saum) continue;
        const f = 1 - (d - r) / (saum - r);
        const licht = clamp(0.35 - (dx * LICHT[0] + dy * LICHT[1]) * 0.65, 0.15, 1);
        const a = f * f * licht;
        if (a * 255 < 6) continue;
        data[i] = farben.glow[0]; data[i + 1] = farben.glow[1]; data[i + 2] = farben.glow[2];
        data[i + 3] = Math.round(a * 210);
        continue;
      }
      const z = Math.sqrt(1 - d2);
      const lambert = Math.max(0, dx * LICHT[0] + dy * LICHT[1] + z * LICHT[2]);
      // Länge und Breite der Kugel — die Oberfläche liegt **auf** ihr, nicht davor.
      const u = (Math.atan2(dx, z) / Math.PI + 1) * 12;
      const v = (Math.asin(clamp(dy, -1, 1)) / Math.PI + 0.5) * 22;
      const punkt = fleck(planet.look, n, u, v, farben);
      // Gerasterte Stufen statt eines Verlaufs: die Schattengrenze wird zu
      // einem Muster, wie in jeder Pixelgrafik.
      const roh = (0.12 + 0.88 * lambert) * STUFEN;
      const stufe = Math.floor(roh) + (roh - Math.floor(roh) > BAYER[(y & 3) * 4 + (x & 3)] ? 1 : 0);
      const hell = Math.min(1, stufe / STUFEN);
      // Am Rand mischt die Atmosphäre mit — die Kugel bekommt einen Horizont.
      const dunst = (1 - z) ** 3 * 0.55;
      const leuchten = Math.max(hell, punkt.glut);
      for (let k = 0; k < 3; k++) {
        const wert = punkt.rgb[k] * leuchten;
        data[i + k] = Math.round(wert + (farben.glow[k] - wert) * dunst * hell);
      }
      data[i + 3] = 255;
    }
  }
  return { size, data };
}

/** Die Farbe einer Kachel auf der Oberflächenansicht — Grundton plus Körnung. */
export function groundShade(planet: Planet, x: number, y: number): number {
  const hell = hex(planet.surface);
  const dunkel = hex(planet.dark);
  // Ein billiges, festes Muster je Kachel: es soll nach Boden aussehen, nicht
  // nach Rauschen, und es darf sich nie bewegen.
  const k = (((x * 73856093) ^ (y * 19349663)) >>> 0) % 7;
  const rgb = mix(dunkel, hell, 0.42 + k * 0.035);
  return (Math.round(rgb[0]) << 16) | (Math.round(rgb[1]) << 8) | Math.round(rgb[2]);
}
